import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type UpdateServiceWorkerFn = (reloadPage?: boolean) => Promise<void> | void;

type PwaUpdateContextValue = {
  needRefresh: boolean;
  offlineReady: boolean;
  checking: boolean;
  lastCheckAt: number | null;
  error: string | null;

  // setters used by PwaUpdater
  setNeedRefresh: (v: boolean) => void;
  setOfflineReady: (v: boolean) => void;
  setRegistration: (r: ServiceWorkerRegistration | null) => void;
  setUpdateServiceWorker: (fn: UpdateServiceWorkerFn | null) => void;

  // actions for UI
  checkForUpdates: () => Promise<boolean>;
  applyUpdate: () => Promise<boolean>;
  checkAndApply: () => Promise<boolean>;
};

const PwaUpdateContext = createContext<PwaUpdateContextValue | null>(null);

export function usePwaUpdate() {
  const ctx = useContext(PwaUpdateContext);
  if (!ctx) {
    throw new Error('usePwaUpdate must be used within PwaUpdateProvider');
  }
  return ctx;
}

export function PwaUpdateProvider({ children }: { children: React.ReactNode }) {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [checking, setChecking] = useState(false);
  const [lastCheckAt, setLastCheckAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);

  const [updateServiceWorker, setUpdateServiceWorkerState] =
    useState<UpdateServiceWorkerFn | null>(null);

  // IMPORTANT:
  // we store the function as a value, so we must wrap it:
  const setUpdateServiceWorker = useCallback(
    (fn: UpdateServiceWorkerFn | null) => {
      setUpdateServiceWorkerState(() => fn);
    },
    [],
  );

  const checkForUpdates = useCallback(async () => {
    setChecking(true);
    setError(null);

    try {
      setLastCheckAt(Date.now());

      const reg =
        registration ??
        (await navigator.serviceWorker?.getRegistration?.()) ??
        null;

      if (!reg) return false;

      await reg.update();
      return Boolean(reg.waiting);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setChecking(false);
    }
  }, [registration]);

  const applyUpdate = useCallback(async () => {
    setError(null);

    try {
      const reg =
        registration ??
        (await navigator.serviceWorker?.getRegistration?.()) ??
        null;

      const hasUpdate = Boolean(reg?.waiting) || needRefresh;
      if (!hasUpdate) return false;

      if (!updateServiceWorker) return false;
      await updateServiceWorker(true);

      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    }
  }, [needRefresh, registration, updateServiceWorker]);

  const checkAndApply = useCallback(async () => {
    const found = await checkForUpdates();
    if (!found && !needRefresh) return false;
    return await applyUpdate();
  }, [applyUpdate, checkForUpdates, needRefresh]);

  const value = useMemo<PwaUpdateContextValue>(
    () => ({
      needRefresh,
      offlineReady,
      checking,
      lastCheckAt,
      error,

      setNeedRefresh,
      setOfflineReady,
      setRegistration,
      setUpdateServiceWorker,

      checkForUpdates,
      applyUpdate,
      checkAndApply,
    }),
    [
      needRefresh,
      offlineReady,
      checking,
      lastCheckAt,
      error,
      setNeedRefresh,
      setOfflineReady,
      setRegistration,
      setUpdateServiceWorker,
      checkForUpdates,
      applyUpdate,
      checkAndApply,
    ],
  );

  return (
    <PwaUpdateContext.Provider value={value}>
      {children}
    </PwaUpdateContext.Provider>
  );
}
